import {
  Syringe, Target, ListChecks, FlaskConical, Droplets, CalendarDays, ScanLine, ClipboardList,
} from 'lucide-react';
import { benTaubOr, BB_DOC, ext } from './benTaubOr';

/*
 * Ben Taub Regional rotation, including the Acute Pain Management Service (APMS).
 * Built from the Blackboard Regional Orientation (orientation guide, AGFA ultrasound
 * workflow, orthopedics no-block list, LA dosing table, LAST algorithm) and the APMS
 * folder (resident to-do, new epidural patient). See docs/blackboard-ben-taub-subspecialties.md.
 * Contacts, Locations and Documents extend the General OR rotation.
 */

const REGIONAL = BB_DOC('_255053_1');
const APMS = BB_DOC('_277962_1');

export const benTaubRegional = {
  accent: benTaubOr.accent,
  icon: Syringe,
  tagline: 'Blocks, epidurals and the acute pain service',
  notesKey: 'notes:ben-taub:regional',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'surgery', title: 'Blocks by Surgery', subtitle: 'No-block list, what each service wants', icon: Target, action: 'guide:surgery', featured: true },
      { id: 'setup', title: 'Block Setup', subtitle: 'Time-out, trays for PNB, catheter, epidural', icon: ListChecks, action: 'guide:setup' },
      { id: 'last', title: 'Local Anesthetics', subtitle: 'Maximum doses and LAST', icon: FlaskConical, action: 'guide:last' },
      { id: 'epidural', title: 'New Epidural', subtitle: 'Pump, cassette, orders, handoff', icon: Droplets, action: 'guide:epidural' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'First-case blocks, planning, leaving', icon: CalendarDays, action: 'guide:day' },
      { id: 'epic', title: 'Epic & Ultrasound', subtitle: 'AGFA order, saving images, block note', icon: ScanLine, action: 'guide:epic' },
      { id: 'apms', title: 'APMS Rounds', subtitle: 'Lists, notes, handoff', icon: ClipboardList, action: 'guide:apms' },
    ] },
  ],

  contacts: {
    storageKey: 'ben-taub-regional-favorites',
    groups: [
      { id: 'regional', title: 'Regional', open: true, items: [
        { label: 'Regional resident', phone: ext('9595') },
        { label: 'Regional faculty', phone: ext('9590'), note: 'Check in at 06:30 for the block count' },
        { label: 'PACU resident', phone: ext('9593'), note: 'Blocks with the Regional 2 attending when there are too many' },
        { label: 'Basement pharmacy', phone: ext('2979'), note: 'Ask for whoever mixes the epidural cassettes' },
      ] },
      ...benTaubOr.contacts.groups,
    ],
  },

  locations: {
    ...benTaubOr.locations,
    groups: [
      { id: 'regional', title: 'Regional', items: [
        { name: 'Block cart', floor: 'OR hallway', code: '1234', codeLabel: 'Cart', note: 'Locked whenever you step away. Nothing left on top' },
        { name: 'Epidural pump key', floor: '4th floor', where: 'Anesthesia tech room', code: '997', codeLabel: 'Pump', note: 'Tubing and batteries are in the epidural carts' },
        { name: 'Central Supply', floor: 'Basement', where: 'Opposite the cafeteria, follow the light-green arrows to the window', note: 'CADD-Solis pumps. Deliveries every 1–2 h, or pick up' },
        { name: 'Epidural cassettes', floor: '4th floor', where: 'PACU pharmacy', note: 'Delivered here from the basement pharmacy' },
      ] },
      ...benTaubOr.locations.groups,
    ],
  },

  documents: {
    storageKey: 'ben-taub-regional-pinned-docs',
    groups: [
      { id: 'regional', title: 'Regional', items: [
        { title: 'Regional orientation', subtitle: 'Orientation guide, ultrasound workflow, no-block list, LA doses, LAST. Blackboard', url: REGIONAL },
        { title: 'APMS orientation', subtitle: 'Rounding, charting, new epidurals. Blackboard', url: APMS },
        { title: 'Block videos: upper extremity', subtitle: 'Blackboard', url: BB_DOC('_255055_1') },
        { title: 'Block videos: lower extremity', subtitle: 'Blackboard', url: BB_DOC('_255057_1') },
        { title: 'Block videos: truncal', subtitle: 'Blackboard', url: BB_DOC('_255059_1') },
        { title: 'NYSORA', subtitle: 'Techniques and anatomy', url: 'https://www.nysora.com' },
      ] },
      ...benTaubOr.documents.groups,
    ],
  },

  guides: {
    surgery: {
      title: 'Blocks by Surgery',
      sections: [
        { title: 'Orthopedics: no block', blocks: [{ type: 'bullets', items: [
          'Tibial shaft fracture managed acutely',
          'Uniplanar external fixation',
          'Knee dislocation',
          'Elbow dislocation or fracture-dislocation',
          'Distal humerus fracture with a posterior approach',
        ] }, { type: 'note', text: 'Tibia: compartment syndrome has to be ruled out. Humerus: radial nerve. Both may be fine for a postoperative block. Bilateral surgery and polytrauma usually mean no block.' }] },
        { title: 'Ask the surgeon first', blocks: [{ type: 'rows', rows: [
          { label: 'Open abdomen', value: 'Epidural or block?', note: 'Bowel resection, prostatectomy, hysterectomy' },
          { label: 'Breast', value: 'PECS blocks?', note: 'Lumpectomy often gets local only' },
          { label: 'Open thoracotomy, open colorectal', value: 'Thoracic epidural', note: 'Part of ERAS' },
          { label: 'Anything', value: 'Preop or postop?', note: 'To protect the start time, a postop block may be the answer' },
        ] }] },
        { title: 'Before every block', blocks: [{ type: 'bullets', items: [
          'Anticoagulation: drugs, last dose, platelets, PT/PTT, TEG',
          'Preop evaluation, consent and IV done. If the OR team is busy, do them yourself',
          'Surgeon has marked the site. The OR nurse has signed off the consent and spoken to the patient before any sedation. No nurse: call the OR front desk for one',
          'Midazolam and fentanyl from the primary OR team, returned afterwards',
        ] }] },
        { title: 'Where', blocks: [{ type: 'bullets', items: [
          'No blocks on the floor. ICU and PACU only. A floor consult comes to the PACU: ask the PACU charge nurse',
        ] }] },
      ],
      sources: [{ title: 'Regional orientation', href: REGIONAL, detail: 'Blackboard, sign-in required' }],
    },

    setup: {
      title: 'Block Setup',
      segments: [
        { id: 'timeout', label: 'Time-out', sections: [
          { title: 'Stop everything', blocks: [{ type: 'bullets', items: [
            'Review the chart first',
            'Engage the patient when you can',
            'Expose the site. Mark every postoperative block',
            'Introductions, name, date of birth, MRN, allergies, procedure, laterality',
            'Everything open and ready before the time-out, then call the Regional faculty',
            'Sterile: gloves, probe cover, and an assistant to position and keep the field',
          ] }] },
        ] },
        { id: 'pnb', label: 'Single shot', sections: [
          { title: 'Tray', blocks: [{ type: 'bullets', items: [
            'ChloraPrep',
            'Sterile sheath with a gel pack, plus a second gel pack',
            'Sterile gloves',
            'TB syringe of local for the skin',
            '20 mL syringe of block local',
            'Block needle: Pajunk 50, 80 or 100 mm, or B Braun 2, 4 or 6 inch',
          ] }] },
        ] },
        { id: 'catheter', label: 'Catheter', sections: [
          { title: 'Tray', blocks: [{ type: 'bullets', items: [
            'Everything from the single-shot tray',
            'B Braun 2 or 4 inch Tuohy with catheter',
            'Mastisol, Steri-Strips, two or more Tegaderms',
          ] }] },
        ] },
        { id: 'epidural', label: 'Epidural', sections: [
          { title: 'Tray', blocks: [{ type: 'bullets', items: [
            'Epidural kit',
            'Spinal needle only for a dural-puncture technique: 5 inch for a 10 cm Tuohy',
            'ChloraPrep, sterile gloves',
            'Mastisol, Steri-Strips, Tegaderm, wide tape',
          ] }] },
        ] },
      ],
      sources: [{ title: 'Regional orientation', href: REGIONAL, detail: 'Blackboard, sign-in required' }],
    },

    last: {
      title: 'Local Anesthetics',
      segments: [
        { id: 'doses', label: 'Maximum doses', sections: [
          { title: 'Per kilogram', confirm: true, blocks: [{ type: 'table', cols: ['Drug', 'Max', 'Cap', 'Duration'], rows: [
            ['Lidocaine', '4–5 mg/kg', '300 mg', '30–90 min'],
            ['Lidocaine with epinephrine', '7 mg/kg', '500 mg', '60–180 min'],
            ['Bupivacaine', '2 mg/kg', '175 mg', '3–8 h'],
            ['Bupivacaine with epinephrine', '3 mg/kg', '225 mg', '3–8 h'],
            ['Mepivacaine', '4–5 mg/kg', '300 mg', '30–120 min'],
            ['Chloroprocaine', '10 mg/kg', '800 mg', '30 min'],
            ['Tetracaine', '1 mg/kg', '', '8 h'],
          ] }, { type: 'note', text: 'Epinephrine lowers bupivacaine toxicity without extending it. Keep the Safe Local app on your phone.' }] },
        ] },
        { id: 'last', label: 'LAST', sections: [
          { title: 'Stop injecting', blocks: [{ type: 'steps', items: [
            'Call for help and the LAST rescue kit',
            '100% oxygen, secure the airway if needed. Avoid hyperventilation',
            'Seizures: benzodiazepines. Avoid large propofol doses',
            'Alert the cardiopulmonary bypass team',
          ] }] },
          { title: 'Lipid emulsion 20%', blocks: [{ type: 'rows', rows: [
            { label: 'Over 70 kg', value: '100 mL over 2–3 min', note: 'Then 200–250 mL over 15–20 min' },
            { label: 'Under 70 kg', value: '1.5 mL/kg over 2–3 min', note: 'Then 0.25 mL/kg/min, ideal body weight' },
            { label: 'Still unstable', value: 'Re-bolus once or twice, double the infusion' },
            { label: 'Ceiling', value: '12 mL/kg', note: 'About 1 L for a resuscitation over 30 min' },
          ] }] },
          { title: 'Circulation', blocks: [{ type: 'bullets', items: [
            { text: 'Epinephrine in boluses of 1 mcg/kg or less', warn: true },
            'No vasopressin, calcium channel blockers, beta blockers or more local anesthetic',
            'Monitor 4–6 h after a cardiovascular event, 2 h after a brief CNS event',
          ] }] },
        ] },
      ],
      sources: [{ title: 'Regional orientation: dosing table and LAST algorithm', href: REGIONAL, detail: 'NYSORA. Blackboard, sign-in required' }],
    },

    epidural: {
      title: 'New Epidural',
      sections: [
        { title: 'After placement', blocks: [{ type: 'steps', items: [
          'Patient List: highlight the patient, Treatment Team, Acute Pain Management',
          'Order CADD-Solis in Orders. Pick it up from Central Supply yourself, or wait for the 1–2 hourly delivery',
          'Epidural order set with the infusion. Comment: YELLOW FACED PUMP',
          'Call the basement pharmacy and ask for the epidural cassette person. The order alone does not reach them. The cassette comes to the 4th floor PACU pharmacy',
          'Set up the pump yourself: medication, tubing, settings. Tubing and batteries in the epidural carts, key in the 4th floor tech room. Deliver it to the OR',
          'Remind the room to chart the infusion start under intraoperative medications',
          'Placement note, cosigned by the regional attending',
          'Update the handoff: placement, rate and level, other analgesics, anticoagulation and VTE prophylaxis',
        ] }, { type: 'note', text: 'The APMS nurse (Karla) usually arranges the pump and cassette for catheters and thoracic epidurals. When she is out, this is you.' }] },
      ],
      sources: [{ title: 'APMS new epidural patient', href: APMS, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'The evening before', blocks: [{ type: 'bullets', items: [
          'Read the OR schedule for blocks and epidurals',
          'NYSORA for the techniques you have not done. Know the maximum doses and the LAST algorithm',
        ] }] },
        { title: 'Morning', blocks: [{ type: 'steps', items: [
          '06:30. Pick up the regional phone',
          'Check in with the Regional faculty: how many first-case blocks and epidurals',
          'See each patient: preop, consent, IV. Help the OR team finish them so the start is not delayed',
          'Ask the attending the order of blocks. First starts may be blocked in the OR',
          'Drugs from the 4th floor OR pharmacy or the 2nd floor PACU Pyxis. Ultrasound, needles, gloves',
          'AGFA order and the patient pulled up on the ultrasound',
        ] }] },
        { title: 'Through the day', blocks: [{ type: 'bullets', items: [
          'Too many blocks at once: a second resident and attending pair, often the PACU resident with Regional 2',
          'Watch the schedule for add-ons that could get a block',
          'Restock the cart and the ultrasound yourself rather than calling the tech',
          'Keep the cart clean, drugs inside, locked when you walk away',
          'After each block: procedure note and onto the APMS follow-up list',
          'Usually done by 16:00. A light afternoon: ask the attending, then call the CON for a formal release. Nobody leaves without the CON or the on-call senior',
        ] }] },
      ],
      sources: [{ title: 'Regional orientation', href: REGIONAL, detail: 'Blackboard, sign-in required' }],
    },

    epic: {
      title: 'Epic & Ultrasound',
      segments: [
        { id: 'order', label: 'Order & note', sections: [
          { title: 'Before the block', blocks: [{ type: 'steps', items: [
            'Manage Orders, AGFA imaging, pick the block. Not listed: ANES IMG – OTHER IMG',
            'On the ultrasound, pick the patient from the worklist so the images file to the chart. Check the name while you scan',
            'Have someone save two or three images during the block. Billing needs them',
          ] }] },
          { title: 'After the block', blocks: [{ type: 'steps', items: [
            'Block note in the Procedures section of the intraoperative record: drugs given and the indication (surgical anesthesia, or postoperative analgesia at the surgeon’s request)',
            'Postprocedure tab, Follow Up Needed, APMS, name the block in the comment',
            'Catheter or thoracic epidural: tell the APMS nurse so the pump and solution get started',
          ] }] },
        ] },
        { id: 'sonosite', label: 'Sonosite', sections: [
          { title: 'Saving to Epic', blocks: [{ type: 'steps', items: [
            'Patient, then Query to refresh the worklist. Pick the patient; two blocks means two entries, image each under its own order',
            '2D to scan. Save for two or three images',
            'Patient, then New/End. That sends the images',
            'Epic Imaging tab shows Final result when they land',
          ] }] },
        ] },
        { id: 'ge', label: 'GE', sections: [
          { title: 'Saving to Epic', blocks: [{ type: 'steps', items: [
            'Patient, Worklist, Query. Pick the patient, then Exam. Choose the block preset',
            'P1 for two or three images',
            'Patient, pick the patient in the worklist, Data Transfer, Export, To AGFA, Transfer',
            'Wait for Spooling complete before powering off',
          ] }] },
        ] },
      ],
      sources: [{ title: 'AGFA ultrasound workflow', href: REGIONAL, detail: 'Blackboard, sign-in required' }],
    },

    apms: {
      title: 'APMS Rounds',
      segments: [
        { id: 'epidurals', label: 'Epidurals', sections: [
          { title: 'List', blocks: [{ type: 'bullets', items: [
            'Patient Lists, System Lists, Anesthesia – Acute Pain Management, BT Acute Pain Management Patients. Patients appear once Acute Pain Management is on the treatment team',
            'Round on every epidural patient with the day’s regional attending',
          ] }] },
          { title: 'Notes', blocks: [{ type: 'bullets', items: [
            'Daily progress note, cosigned: Follow-up tab, Follow-up Evaluations, ANE ACUTE PAIN PROGRESS NOTE HCHD IP. Or copy the handoff to a note with Alt+O',
            'Quick note for any change: bolus, rate. No cosign needed',
            'Catheter out and signed off: end treatment to drop the patient from the list',
          ] }] },
          { title: 'Handoff', blocks: [{ type: 'bullets', items: [
            'Write Handoff and Print Handoff live on the Patient List, not in the chart',
            'Placement details, current rate and level, other analgesics, anticoagulation and VTE prophylaxis',
            'Print it and pass it with the pager',
          ] }] },
        ] },
        { id: 'blocks', label: 'Blocks & ERAS', sections: [
          { title: 'List', blocks: [{ type: 'bullets', items: [
            'Patient Lists, System Lists, Anesthesia Follow-ups, BT Anesthesia. Follow-Up Service APMS for single shots, ERAS for ERAS patients',
            'Add every single-shot block and every ERAS patient on the day of service, block or not',
          ] }] },
          { title: 'Round, POD 1', blocks: [{ type: 'bullets', items: [
            'Did the block work, and any complication such as nerve injury. The block has usually worn off; the surgical team manages pain unless an epidural is running. Offer recommendations',
            'Block as the primary anesthetic: quick note, no cosign',
            'Block for postoperative analgesia (LMA or tube used): faculty sees the patient and cosigns',
            'ERAS without a block: still a quick note on POD 1',
            'Quick progress note, or ANE POST ANESTHESIA EVALUATION HCHD IP with Anesthesia Type Regional, or the APMS nurse’s SmartLink note. Then Follow-up Complete',
            'Single shots leave the list after POD 1; catheters after removal',
          ] }] },
          { title: 'When the APMS nurse is out', blocks: [{ type: 'bullets', items: [
            'Take the APMS pager from the overnight OB senior and round on APMS patients with the regional attending',
            'Use the ANE Acute Pain Progress Note template; addend the record if it is closed',
          ] }] },
        ] },
      ],
      sources: [{ title: 'APMS resident to-do', href: APMS, detail: 'Blackboard, sign-in required' }],
    },
  },
};
